import { useEffect, useState } from "react";
import { Badge, ProgressBar, Nav, Tab, Collapse, Button } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ActivityTrace, Job, NetworkError } from "@models/job.model";
import { JobService } from "@services/jobs/jobService";
import JsonDiv from "@components/JsonDiv";
import copy from "copy-text-to-clipboard";

function getStatusColor(status: string) {
    const s = status?.toLowerCase?.() || "";
    if (s === "failed" || s === "error") return "danger";
    if (s === "completed" || s === "success") return "success";
    if (s === "running" || s === "in_progress" || s === "processing") return "primary";
    if (s === "created" || s === "queued" || s === "pending" || s === "warning") return "warning";
    return "secondary";
}

function isInProgress(status: string) {
    const s = status?.toLowerCase?.() || "";
    return ["running", "in_progress", "processing", "queued", "pending", "started"].includes(s);
}

function progressVariant(pct: number, status: string) {
    const s = status?.toLowerCase?.() || "";
    if (s === "failed" || s === "error") return "danger";
    if (s === "completed" || s === "success") return "success";
    if (s === "warning") return "warning";
    if (pct >= 66) return "primary";
    if (pct >= 33) return "info";
    return "secondary";
}

interface JobDetailPanelProps {
    job: Job;
}

export default function JobDetailPanel({ job }: JobDetailPanelProps) {
    // Where a fix link should return to: this page exactly as it stands, which already
    // carries the list's filters and paging and the selected job in its query string.
    const location = useLocation();
    const navigate = useNavigate();
    const [resuming, setResuming] = useState(false);
    const [resumeError, setResumeError] = useState<string | null>(null);
    // A "Reprocess all files" a deploy killed can pick up where it stopped (MPN-1907).
    // Whether it still may is the new job's call; it fails with the reason if not.
    const canResume = job.type === "RESET_SEGMENT_DATA"
        && job.status?.toUpperCase() === "FAILED"
        && !!job.input?.replay;

    const resume = async () => {
        setResuming(true);
        setResumeError(null);
        try {
            const started = await new JobService().resumeJob(job.id);
            navigate(`${import.meta.env.BASE_URL}jobs/${started.id}`);
        } catch (err) {
            setResumeError(err instanceof Error ? err.message : "Resume failed to start");
        } finally {
            setResuming(false);
        }
    };
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    // Persisted traces (local dev + older jobs) win. Newer jobs no longer
    // persist traces to job_context — fall back to the CloudWatch proxy.
    const persistedTraces = job.job_context?.traces ?? [];
    const [cwTraces, setCwTraces] = useState<ActivityTrace[]>([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [networkErrors, setNetworkErrors] = useState<NetworkError[]>([]);
    const [loadingNetworkErrors, setLoadingNetworkErrors] = useState(false);
    const traces = persistedTraces.length > 0 ? persistedTraces : cwTraces;

    // All expandable traces start expanded
    const allTraceIds = new Set(traces.map((t, i) => t.id || String(i)));
    const [expandedTraces, setExpandedTraces] = useState<Set<string>>(allTraceIds);

    useEffect(() => {
        if (persistedTraces.length > 0) return;
        let cancelled = false;
        setLoadingLogs(true);
        new JobService().getJobLogs(job.id)
            .then((logs) => {
                if (cancelled) return;
                setCwTraces(logs);
                setExpandedTraces(new Set(logs.map((t, i) => t.id || String(i))));
            })
            .finally(() => { if (!cancelled) setLoadingLogs(false); });
        return () => { cancelled = true; };
    }, [job.id]);

    useEffect(() => {
        let cancelled = false;
        setLoadingNetworkErrors(true);
        new JobService().getJobNetworkErrors(job.id)
            .then((errors) => { if (!cancelled) setNetworkErrors(errors); })
            .finally(() => { if (!cancelled) setLoadingNetworkErrors(false); });
        return () => { cancelled = true; };
    }, [job.id]);

    const toggleTraceExpansion = (traceId: string) => {
        setExpandedTraces((prev) => {
            const next = new Set(prev);
            if (next.has(traceId)) {
                next.delete(traceId);
            } else {
                next.add(traceId);
            }
            return next;
        });
    };

    const userName = [job.user?.employee?.person?.first_name, job.user?.employee?.person?.last_name]
        .filter(Boolean).join(" ");

    return (
        <div className="job-detail-panel">
            {/* Compact header */}
            <div className="job-detail-panel__header">
                <div className="job-detail-panel__title-row">
                    <span className="job-detail-panel__type">{job.name || job.type}</span>
                    <span className="job-detail-panel__id">#{job.id}</span>
                    <Badge bg={getStatusColor(job.status)} className="job-detail-panel__status">
                        {job.status}
                    </Badge>
                    {canResume && (
                        <Button
                            size="sm"
                            variant="outline-primary"
                            className="ms-auto"
                            onClick={resume}
                            disabled={resuming}
                            title="Finish this rebuild from its last finished date, without deleting anything"
                        >
                            {resuming ? "Resuming…" : "Resume"}
                        </Button>
                    )}
                </div>
                {resumeError && <div className="text-danger small">{resumeError}</div>}
                <div className="job-detail-panel__meta">
                    {userName && <span>Created by <strong>{userName}</strong></span>}
                    <span>{new Date(job.create_date).toLocaleString()}</span>
                    <span>Updated {new Date(job.update_date).toLocaleTimeString()}</span>
                </div>
                {/* What the jobs list showed to get you here (MPN-1636). A job opened
                    from a failing publish otherwise names no engagement, no campaign and
                    no way back to the sibling networks in its batch. */}
                {(job.engagement_name || job.campaign_name || job.network
                  || (job.batch_id && job.batch_id !== job.id)) && (
                    <div className="job-detail-panel__context">
                        {job.engagement_name && (
                            <Link
                                className="job-detail-panel__chip"
                                to={`${import.meta.env.BASE_URL}engagements/${job.engagement_id}?from=${returnTo}`}
                            >
                                <i className="bi bi-briefcase" />
                                {job.engagement_name}
                            </Link>
                        )}
                        {job.campaign_name && (
                            <Link
                                className="job-detail-panel__chip"
                                to={`${import.meta.env.BASE_URL}engagements/${job.engagement_id}/content?campaign=${job.campaign_id}&from=${returnTo}`}
                            >
                                <i className="bi bi-megaphone" />
                                {job.campaign_name}
                            </Link>
                        )}
                        {job.network && (
                            <span className="job-detail-panel__chip job-detail-panel__chip--network">
                                {job.network}
                            </span>
                        )}
                        {job.batch_id && job.batch_id !== job.id && (
                            <Link
                                className="job-detail-panel__chip"
                                to={`${import.meta.env.BASE_URL}jobs/${job.batch_id}`}
                            >
                                <i className="bi bi-diagram-2" />
                                batch {job.batch_id}
                            </Link>
                        )}
                    </div>
                )}
                <ProgressBar
                    className="job-detail-panel__progress"
                    now={job.percent_complete}
                    variant={progressVariant(job.percent_complete, job.status)}
                    striped={isInProgress(job.status)}
                    animated={isInProgress(job.status)}
                />
            </div>

            {/* Tabs */}
            <Tab.Container defaultActiveKey="activities">
                <div className="job-detail-panel__tabs-header">
                    <Nav variant="tabs">
                        <Nav.Item><Nav.Link eventKey="activities">Activities</Nav.Link></Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="network-errors">
                                Network Errors
                                {networkErrors.length > 0 && (
                                    <Badge bg="danger" className="ms-2">{networkErrors.length}</Badge>
                                )}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item><Nav.Link eventKey="input">Input</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="output">Output</Nav.Link></Nav.Item>
                    </Nav>
                </div>

                <Tab.Content className="job-detail-panel__tab-content">
                    {/* Activities Tab */}
                    <Tab.Pane eventKey="activities" className="px-3 py-0">
                        {traces.length > 0 ? (
                            <div>
                                {traces.map((trace, index) => {
                                    const traceId = trace.id || String(index);
                                    const hasLogs = !!(trace.log && trace.log.length > 0);
                                    const hasError = !!(trace.error_detail && trace.error_detail.trim());
                                    const canExpand = hasLogs || hasError;
                                    const isExpanded = expandedTraces.has(traceId);
                                    const lastError = hasError
                                        ? trace.error_detail.split("\n").filter((l: string) => l.trim()).pop() ?? null
                                        : null;
                                    const isLast = index === traces.length - 1;
                                    const isFailed = trace.status === "FAILED";

                                    return (
                                        <div key={traceId} className={isFailed ? "job-detail__activity--failed" : ""}>
                                            <div className={`job-detail__activity-row ${isLast && !isExpanded ? "" : "border-bottom"}`}>
                                                <div style={{ width: "30px", flexShrink: 0 }}>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className={`p-0 text-muted ${!canExpand ? "job-detail__chevron-placeholder" : ""}`}
                                                        disabled={!canExpand}
                                                        onClick={() => canExpand && toggleTraceExpansion(traceId)}
                                                    >
                                                        <i className={`bi bi-chevron-${isExpanded ? "down" : "right"} fs-16`} />
                                                    </Button>
                                                </div>
                                                <div className="job-detail__activity-content">
                                                    <div>{trace.name}</div>
                                                    {lastError && (
                                                        <small className="text-danger font-monospace text-truncate d-block job-detail__activity-error">
                                                            {lastError}
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="job-detail__activity-status-badge">
                                                    <Badge bg={getStatusColor(trace.status)}>{trace.status}</Badge>
                                                </div>
                                            </div>
                                            <Collapse in={isExpanded && canExpand}>
                                                <div className="border-bottom">
                                                    {hasLogs && (
                                                        <div className="job-detail__trace-logs">
                                                            {trace.log.map((logEntry, logIndex) => (
                                                                <div key={logIndex} className="job-detail__trace-log-line">{logEntry}</div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {hasError && (
                                                        <div className="job-detail__trace-error">{trace.error_detail}</div>
                                                    )}
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="job-detail__activity-row">
                                <div style={{ width: "30px", flexShrink: 0 }}>
                                    <Button variant="link" size="sm" className="p-0 text-muted job-detail__chevron-placeholder" disabled>
                                        <i className="bi bi-chevron-right fs-16" />
                                    </Button>
                                </div>
                                <div className="job-detail__activity-content">
                                    <div className="text-muted">{loadingLogs ? "Loading activity logs…" : "No activities yet"}</div>
                                </div>
                                <div className="job-detail__activity-status-badge" />
                            </div>
                        )}
                    </Tab.Pane>

                    {/* Network Errors Tab — the networks' own answer, which the
                        Activities tab buries inside the raw log line (MPN-1337). */}
                    <Tab.Pane eventKey="network-errors">
                        <div className="p-3">
                            {networkErrors.length > 0 ? (
                                networkErrors.map((error, index) => (
                                    <div
                                        key={index}
                                        className={`job-detail__network-error ${error.transient ? "job-detail__network-error--transient" : ""}`}
                                    >
                                        <div className="job-detail__network-error-head">
                                            <Badge bg="secondary">{error.network}</Badge>
                                            {error.code && (
                                                <span className="job-detail__network-error-code">{error.code}</span>
                                            )}
                                            {error.transient && <Badge bg="warning">Transient — retry</Badge>}
                                        </div>
                                        {error.title && (
                                            <div className="job-detail__network-error-title">{error.title}</div>
                                        )}
                                        <div className="job-detail__network-error-summary">{error.summary}</div>
                                        {error.fix && (
                                            <div className="job-detail__network-error-fix">
                                                <Link
                                                    to={`${import.meta.env.BASE_URL}${error.fix.path}${
                                                        error.fix.path.includes("?") ? "&" : "?"
                                                    }from=${returnTo}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    {error.fix.label}
                                                    <i className="bi bi-arrow-right ms-2" />
                                                </Link>
                                            </div>
                                        )}
                                        {(error.context || error.span_name) && (
                                            <div className="job-detail__network-error-context">
                                                {error.span_name && <>During <strong>{error.span_name}</strong>. </>}
                                                {error.context && <>We logged <code>{error.context}</code></>}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-muted text-center p-4">
                                    {loadingNetworkErrors ? "Loading network errors…" : "No network errors on this job"}
                                </div>
                            )}
                        </div>
                    </Tab.Pane>

                    {/* Input Tab */}
                    <Tab.Pane eventKey="input">
                        <div className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Job Input</h6>
                                {job.input && (
                                    <Button variant="outline-secondary" size="sm" onClick={() => copy(JSON.stringify(job.input, null, 2))}>
                                        <i className="bi bi-clipboard me-1" /> Copy JSON
                                    </Button>
                                )}
                            </div>
                            {job.input ? <JsonDiv data={job.input} /> : <div className="text-muted text-center p-4">No input data</div>}
                        </div>
                    </Tab.Pane>

                    {/* Output Tab */}
                    <Tab.Pane eventKey="output">
                        <div className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Job Output</h6>
                                {job.job_context?.output && (
                                    <Button variant="outline-secondary" size="sm" onClick={() => copy(JSON.stringify(job.job_context.output, null, 2))}>
                                        <i className="bi bi-clipboard me-1" /> Copy JSON
                                    </Button>
                                )}
                            </div>
                            {job.job_context?.output ? <JsonDiv data={job.job_context.output} /> : <div className="text-muted text-center p-4">No output data</div>}
                        </div>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
}
